<template>
  <div class="side-pnl-chart">
    <div class="side-pnl-head">
      <h3>Net PnL por Ponta</h3>
      <div class="side-pnl-controls">
        <label class="side-pnl-check">
          <input type="checkbox" v-model="includeSpot" />
          Spot
        </label>
        <label class="side-pnl-check">
          <input type="checkbox" v-model="includeFutures" />
          Futuros
        </label>
        <strong class="side-pnl-last" style="color: #38bdf8">Net {{ lastNetLabel }}</strong>
        <strong class="side-pnl-last" style="color: #26a69a">Long {{ lastLongLabel }}</strong>
        <strong class="side-pnl-last" style="color: #a78bfa">Short {{ lastShortLabel }}</strong>
      </div>
    </div>
    <p v-if="!hasSelection" class="side-pnl-empty">Selecione spot, futuros ou os dois.</p>
    <div v-else-if="points.length" class="side-pnl-plot">
      <apexchart
        :key="chartKey"
        type="line"
        height="100%"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
    <p v-else class="side-pnl-empty">Nenhum evento do período com snapshot.</p>
  </div>
</template>

<script>
import { defaultChartOptions } from '@/utils/globalApexConfig';
import { sidePnlFromSnapshot } from '@/utils/computeEdgesSeries.js';

function formatSignedUsd(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(Math.round(n)).toLocaleString('en-US');
  if (n < 0) return `-$${abs}`;
  if (n > 0) return `+$${abs}`;
  return '$0';
}

function plotValue(value) {
  return Number.isFinite(value) ? Number(value.toFixed(2)) : null;
}

export default {
  name: 'EvolNetPnlChart',
  props: {
    timeSeries: { type: Array, required: true },
    aggLabels: { type: Array, required: true },
  },
  data() {
    return {
      includeSpot: true,
      includeFutures: true,
    };
  },
  computed: {
    hasSelection() {
      return this.includeSpot || this.includeFutures;
    },
    points() {
      if (!this.hasSelection) return [];
      return (this.timeSeries || []).map(({ index, m }) => ({
        index,
        ...sidePnlFromSnapshot(m?.manualsSnapshot, m?.currentPrice, {
          spot: this.includeSpot,
          futures: this.includeFutures,
        }),
      }));
    },
    lastPoint() {
      return this.points[this.points.length - 1] || null;
    },
    lastNetLabel() {
      return formatSignedUsd(this.lastPoint?.netPnl);
    },
    lastLongLabel() {
      return formatSignedUsd(this.lastPoint?.longPnl);
    },
    lastShortLabel() {
      return formatSignedUsd(this.lastPoint?.shortPnl);
    },
    chartKey() {
      const last = this.lastPoint;
      return `side-pnl-${this.includeSpot}-${this.includeFutures}-${this.points.length}-${last?.netPnl ?? 0}-${last?.longPnl ?? 0}-${last?.shortPnl ?? 0}`;
    },
    chartSeries() {
      return [
        {
          name: 'Net PnL',
          data: this.points.map((point) => [point.index, plotValue(point.netPnl)]),
        },
        {
          name: 'PnL Long',
          data: this.points.map((point) => [point.index, plotValue(point.longPnl)]),
        },
        {
          name: 'PnL Short',
          data: this.points.map((point) => [point.index, plotValue(point.shortPnl)]),
        },
      ];
    },
    chartOptions() {
      const self = this;
      return {
        ...defaultChartOptions,
        colors: ['#38bdf8', '#26a69a', '#a78bfa'],
        legend: { ...defaultChartOptions.legend },
        stroke: { curve: 'straight', width: [2.5, 2, 2] },
        markers: { size: 0, hover: { size: 4 }, strokeWidth: 0 },
        grid: {
          ...defaultChartOptions.grid,
          padding: { right: 24, left: 12, top: 8 },
        },
        xaxis: { type: 'numeric', labels: { formatter: this.xAxisFormatter } },
        yaxis: {
          labels: { formatter: (val) => formatSignedUsd(val) },
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom({ dataPointIndex }) {
            return self.tooltipHtml(dataPointIndex);
          },
        },
      };
    },
  },
  methods: {
    xAxisFormatter(val) {
      return this.aggLabels[Math.floor(val)] || '';
    },
    tooltipHtml(dataPointIndex) {
      const point = this.points[dataPointIndex];
      if (!point) return '';
      const label = this.aggLabels[dataPointIndex] || '';
      return `
        <div style="background:#1e1e1e;color:#fff;padding:10px 14px;border:1px solid #444;border-radius:6px;font-size:12px;min-width:200px;">
          <div style="font-weight:600;margin-bottom:8px;border-bottom:1px solid #444;padding-bottom:4px;color:#38bdf8;">${label}</div>
          <div style="line-height:1.7;">
            <div style="color:#38bdf8;">Net PnL: <b>${formatSignedUsd(point.netPnl)}</b></div>
            <div style="color:#26a69a;">PnL Long: <b>${formatSignedUsd(point.longPnl)}</b></div>
            <div style="color:#a78bfa;">PnL Short: <b>${formatSignedUsd(point.shortPnl)}</b></div>
          </div>
        </div>
      `;
    },
  },
};
</script>

<style scoped>
.side-pnl-chart {
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background-color: var(--table-header-bgcolor);
  border-radius: 10px;
  padding: 16px 16px 8px;
}

.side-pnl-head h3 {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #f1f1f1;
}

.side-pnl-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 8px;
}

.side-pnl-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--text-color, #f1f1f1);
  cursor: pointer;
}

.side-pnl-check input[type="checkbox"] {
  margin: 0;
}

.side-pnl-last {
  font-size: 15px;
  font-weight: 700;
}

.side-pnl-plot {
  height: calc(100% - 78px);
}

.side-pnl-empty {
  margin: 0;
  opacity: 0.7;
}
</style>
